// src/components/navbar/MobileDrawer.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Collapse,
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { getOfficialDocsConfig } from "../../services/officialDocsService";

const MobileDrawer = ({
  drawerOpen,
  toggleDrawer,
  menuItems,
  user,
  navigate,
  onLogout,
  location,
}) => {
  // Estado de acordeones (por texto del item)
  const [openMap, setOpenMap] = useState({});

  // ===== Config "Documentación" (backend) =====
  const [docsCfg, setDocsCfg] = useState(null);
  const [docsLoading, setDocsLoading] = useState(true);
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const cfg = await getOfficialDocsConfig();
        if (active) setDocsCfg(cfg);
      } catch {
        if (active) setDocsCfg(null);
      } finally {
        if (active) setDocsLoading(false);
      }
    };
    load();
    const onCfgUpdated = () => load();
    window.addEventListener("official-docs-config-updated", onCfgUpdated);
    return () => {
      active = false;
      window.removeEventListener("official-docs-config-updated", onCfgUpdated);
    };
  }, []);

  // Rutas base (igual que DesktopMenu)
  const DOC_LINKS = [
    { key: "showPublicAccount",     label: "Cuenta pública",                  path: "/annual-reports" },
    { key: "showEstatutos",         label: "Estatutos",                       path: "/statutes" },
    { key: "showCodigoEtica",       label: "Código de ética",                 path: "/ethics-code" },
    { key: "showModeloPrevencion",  label: "Modelo de prevención del delito", path: "/crime-prevention" },
    { key: "showReglamentoInterno", label: "Reglamento interno",              path: "/internal-regulation" },
    { key: "showProtocolos",        label: "Protocolos",                      path: "/protocols" },
    { key: "showMaterialInteres",   label: "Material de interés",             path: "/material-content" },
  ];

  // Grupos
  const OFFICIAL_KEYS = new Set([
    "showPublicAccount",
    "showEstatutos",
    "showCodigoEtica",
    "showModeloPrevencion",
    "showReglamentoInterno",
    "showProtocolos",
  ]);
  const officialBase = DOC_LINKS.filter(l => OFFICIAL_KEYS.has(l.key));
  const materialItem = DOC_LINKS.find(l => l.key === "showMaterialInteres");

  const visibleOfficialLinks = useMemo(() => {
    if (isAdmin) return officialBase;
    if (!docsCfg) return [];
    return officialBase.filter((l) => {
      if (l.key === "showPublicAccount") {
        return !!(docsCfg.showPublicAccount || docsCfg.showAnnualReports);
      }
      return !!docsCfg[l.key];
    });
  }, [isAdmin, docsCfg]);

  const showMaterialEntry =
    !!materialItem && (isAdmin || (!docsLoading && docsCfg?.showMaterialInteres));

  const showDocsMenu =
    isAdmin ||
    (!docsLoading &&
      docsCfg?.docsMenuEnabled &&
      (visibleOfficialLinks.length > 0 || showMaterialEntry));

  // items visibles - reemplazar "INFORMACIÓN FINANCIERA" con "DOCUMENTACIÓN" si aplica
  const items = useMemo(() => {
    const baseItems = menuItems || [];
    if (!showDocsMenu) return baseItems;
    
    // Reemplazar el grupo "INFORMACIÓN FINANCIERA" con "DOCUMENTACIÓN"
    return baseItems.map((item) => {
      if (item.text === "INFORMACIÓN FINANCIERA") {
        return {
          text: "DOCUMENTACIÓN",
          icon: item.icon,
          children: [
            ...(showMaterialEntry ? [{ text: materialItem.label, path: materialItem.path, icon: item.icon }] : []),
            ...(visibleOfficialLinks.length > 0 ? [{
              text: "Documentación oficial",
              children: visibleOfficialLinks.map(l => ({ text: l.label, path: l.path, icon: item.icon }))
            }] : [])
          ]
        };
      }
      return item;
    });
  }, [menuItems, showDocsMenu, showMaterialEntry, materialItem, visibleOfficialLinks]);

  const isActive = (path) => path && location.pathname === path;

  const isGroupActive = (group) =>
    Array.isArray(group?.children) &&
    group.children.some((c) => isActive(c.path));

  const toggleGroup = (key) =>
    setOpenMap((m) => ({ ...m, [key]: !m[key] }));

  const go = (path) => {
    if (!path) return;
    navigate(path);
    // cerrar drawer tras navegar
    setTimeout(() => toggleDrawer(false)({ type: "click" }), 0);
  };

  return (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={toggleDrawer(false)}
      PaperProps={{
        sx: {
          backgroundColor: "#09274A",
          width: 260,
        },
      }}
    >
      {/* Contenido (no cerrar al hacer click interno) */}
      <div className="w-full" role="presentation">
        {/* Header con nombre de usuario/negocio */}
        <div className="p-4">
          <Typography
            variant="h6"
            sx={{
              color: "white",
              fontFamily: "Nunito, sans-serif",
              fontSize: "1.25rem",
              cursor: user ? "pointer" : "default",
            }}
            onClick={() => user && go("/profile")}
          >
            {user ? user.business_name || user.name : "No Registrado"}
          </Typography>
        </div>

        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />

        <List>
          {items.map((item) => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const key = item.text;
            const groupOpen = !!openMap[key];
            const groupActive = hasChildren && isGroupActive(item);

            // Ítem con submenú (acordeón)
            if (hasChildren) {
              return (
                <React.Fragment key={key}>
                  <ListItem
                    button
                    onClick={() => toggleGroup(key)}
                    sx={{
                      "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.3)" },
                      ...(groupActive && { backgroundColor: "rgba(220, 52, 101, 0.15)" }),
                    }}
                  >
                    {item.icon && (
                      <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                        {item.icon}
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        color: "white",
                        fontFamily: "Nunito, sans-serif",
                        fontSize: "1rem",
                        fontWeight: groupActive ? 600 : 400,
                      }}
                    />
                    {groupOpen ? (
                      <ExpandLess sx={{ color: "white" }} />
                    ) : (
                      <ExpandMore sx={{ color: "white" }} />
                    )}
                  </ListItem>

                  <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.children.map((child) => {
                        // Si el child tiene children, es un submenú anidado
                        const hasSubChildren = Array.isArray(child.children) && child.children.length > 0;
                        const subKey = `${key}-${child.text}`;
                        const subOpen = !!openMap[subKey];
                        const subActive = hasSubChildren && child.children.some((c) => isActive(c.path));
                        const active = !hasSubChildren && isActive(child.path);

                        if (hasSubChildren) {
                          return (
                            <React.Fragment key={subKey}>
                              <ListItem
                                button
                                onClick={() => toggleGroup(subKey)}
                                sx={{
                                  pl: 5,
                                  "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.28)" },
                                  ...(subActive && { backgroundColor: "rgba(220, 52, 101, 0.18)" }),
                                }}
                              >
                                {child.icon && (
                                  <ListItemIcon sx={{ color: "white", minWidth: 32, opacity: 0.9 }}>
                                    {child.icon}
                                  </ListItemIcon>
                                )}
                                <ListItemText
                                  primary={child.text}
                                  primaryTypographyProps={{
                                    color: "white",
                                    fontFamily: "Nunito, sans-serif",
                                    fontSize: ".98rem",
                                    fontWeight: subActive ? 600 : 400,
                                  }}
                                />
                                {subOpen ? (
                                  <ExpandLess sx={{ color: "white", fontSize: 18 }} />
                                ) : (
                                  <ExpandMore sx={{ color: "white", fontSize: 18 }} />
                                )}
                              </ListItem>
                              <Collapse in={subOpen} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                  {child.children.map((subChild) => {
                                    const subActive = isActive(subChild.path);
                                    return (
                                      <ListItem
                                        key={subChild.text}
                                        button
                                        onClick={() => go(subChild.path)}
                                        selected={subActive}
                                        sx={{
                                          pl: 8,
                                          "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.25)" },
                                          "&.Mui-selected": { backgroundColor: "rgba(220, 52, 101, 0.15)" },
                                        }}
                                      >
                                        {subChild.icon && (
                                          <ListItemIcon sx={{ color: "white", minWidth: 28, opacity: 0.85 }}>
                                            {subChild.icon}
                                          </ListItemIcon>
                                        )}
                                        <ListItemText
                                          primary={subChild.text}
                                          primaryTypographyProps={{
                                            color: "white",
                                            fontFamily: "Nunito, sans-serif",
                                            fontSize: ".95rem",
                                            fontWeight: subActive ? 600 : 400,
                                          }}
                                        />
                                      </ListItem>
                                    );
                                  })}
                                </List>
                              </Collapse>
                            </React.Fragment>
                          );
                        }

                        // Item simple sin submenú
                        return (
                          <ListItem
                            key={child.text}
                            button
                            onClick={() => go(child.path)}
                            selected={active}
                            sx={{
                              pl: 5,
                              "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.28)" },
                              "&.Mui-selected": { backgroundColor: "rgba(220, 52, 101, 0.18)" },
                            }}
                          >
                            {child.icon && (
                              <ListItemIcon sx={{ color: "white", minWidth: 32, opacity: 0.9 }}>
                                {child.icon}
                              </ListItemIcon>
                            )}
                            <ListItemText
                              primary={child.text}
                              primaryTypographyProps={{
                                color: "white",
                                fontFamily: "Nunito, sans-serif",
                                fontSize: ".98rem",
                                fontWeight: active ? 600 : 400,
                              }}
                            />
                          </ListItem>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            // Ítem simple
            return (
              <ListItem
                key={key}
                button
                onClick={() => go(item.path)}
                selected={isActive(item.path)}
                sx={{
                  "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.3)" },
                  "&.Mui-selected": { backgroundColor: "rgba(220, 52, 101, 0.15)" },
                }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ color: "white", minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    color: "white",
                    fontFamily: "Nunito, sans-serif",
                    fontSize: "1rem",
                    fontWeight: isActive(item.path) ? 600 : 400,
                  }}
                />
              </ListItem>
            );
          })}

          {/* Mi Perfil */}
          {user && (
            <>
              <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)", my: 1 }} />
              <ListItem
                button
                onClick={() => go("/profile")}
                selected={isActive("/profile")}
                sx={{
                  "&:hover": { backgroundColor: "rgba(4, 136, 175, 0.3)" },
                  "&.Mui-selected": { backgroundColor: "rgba(220, 52, 101, 0.15)" },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AccountCircleIcon sx={{ color: "white" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Mi Perfil"
                  primaryTypographyProps={{
                    color: "white",
                    fontFamily: "Nunito, sans-serif",
                    fontSize: "1rem",
                    fontWeight: isActive("/profile") ? 600 : 400,
                  }}
                />
              </ListItem>
            </>
          )}

          {/* Logout */}
          {user && (
            <ListItem
              button
              onClick={onLogout}
              sx={{ "&:hover": { backgroundColor: "rgba(220, 52, 101, 0.3)" } }}
            >
              <ListItemIcon sx={{ minWidth: 38 }}>
                <LogoutIcon sx={{ color: "#DC3465" }} />
              </ListItemIcon>
              <ListItemText
                primary="Salir"
                primaryTypographyProps={{
                  color: "#DC3465",
                  fontFamily: "Nunito, sans-serif",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              />
            </ListItem>
          )}
        </List>
      </div>
    </Drawer>
  );
};

export default React.memo(MobileDrawer);
